import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { listOrganisations } from "../lib/api";
import { clientError } from "../lib/clientMessage";
import { loadPmoReport, type PmoReportLoad } from "../lib/pmoReportLoader";
import { PmoClientReport } from "../respond/PmoClientReport";
import { useBreadcrumb } from "../shell/ContextHeader";

// The client report for the organisation's owner and for Kenneth — the same
// report the respondent sees at /respond/report, through the same loader and
// the same component, so the two can never differ. "Download PDF report"
// (dashboard proposal V1.1 §4) opens this route with ?print=1: the browser's
// print dialog is the MVP's PDF, as decision §5 says.

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "not-submitted"; orgId: string; runId: string }
  | { kind: "ready"; load: Extract<PmoReportLoad, { kind: "ready" }> };

export function ClientReport() {
  const { orgId, runId } = useParams<{ orgId: string; runId: string }>();
  const [params] = useSearchParams();
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<State>({ kind: "loading" });
  const autoPrint = params.get("print") === "1";

  useEffect(() => {
    if (!orgId || !runId) return;
    void (async () => {
      try {
        const [orgs, load] = await Promise.all([listOrganisations(), loadPmoReport(runId)]);
        const org = orgs.find((o) => o.id === orgId);
        if (!org || !load || load.run.organisation_id !== orgId) {
          setState({ kind: "error", message: "run not found, or you are not a member" });
          return;
        }
        setTrail([
          { label: "Clients & engagements", to: "/clients" },
          { label: org.name, to: `/clients/${org.id}` },
          { label: load.run.title ?? "Untitled run", to: `/clients/${org.id}/runs/${load.run.id}` },
          { label: "Report" },
        ]);
        if (load.kind !== "ready") {
          setState({ kind: "not-submitted", orgId, runId });
          return;
        }
        setState({ kind: "ready", load });
      } catch (e) {
        setState({ kind: "error", message: clientError("report", e).message });
      }
    })();
  }, [orgId, runId, setTrail]);

  if (state.kind === "loading") return <p>Preparing the report…</p>;
  if (state.kind === "error") return <div className="error-banner" role="alert">{state.message}</div>;
  if (state.kind === "not-submitted") {
    return (
      <div className="card empty-state">
        <h3>The report is not ready yet</h3>
        <p>The report is produced when the assessment is submitted.</p>
        <Link className="btn btn-secondary" to={`/clients/${state.orgId}/runs/${state.runId}/dashboard`}>Open dashboard</Link>
      </div>
    );
  }
  return <PmoClientReport model={state.load.model} scale={state.load.scale} autoPrint={autoPrint} />;
}
